import type { FleetV2Response } from '@emma/api_client/types';
import { Button, Flex, Icon, Menu, Portal, Text, Tooltip } from '@lego/klik-ui';
import { PlusBold } from '@lego/klik-ui/icons';
import { useMemo, useState } from 'react';
import { useApiMutation } from '@/hooks/api';
import {
  useDeleteFleetInvitation,
  useFleetInvitations,
  useFleetInvitationsByFleet,
} from '@/hooks/fleet';
import { useAllWorkspaces } from '@/hooks/workspace';
import { Loader } from '../common';
import { fleetInvitationTableColumns } from '../common/table/column_definitions/FleetInvitation';
import { TableStoreProvider } from '../common/table/store/Context';
import { Table } from '../common/table/Table';

type Props = {
  fleet?: FleetV2Response;
};

export const FleetInvitationsTable = ({ fleet }: Props) => {
  if (fleet?.id) {
    return <FleetInvitationsTableByFleet fleet={fleet} />;
  }
  return <FleetInvitationsTableAll />;
};

const FleetInvitationsTableByFleet = ({ fleet }: { fleet: FleetV2Response }) => {
  const { data, isLoading, mutate, isValidating } = useFleetInvitationsByFleet(
    fleet.workspaceId,
    fleet.id
  );
  const deleteFleetInvitation = useDeleteFleetInvitation(fleet.workspaceId, fleet.id);
  const [deletingIds, setDeletingIds] = useState<Set<number>>(new Set());

  const handleDelete = async (invitationId: number) => {
    setDeletingIds((prev) => new Set(prev).add(invitationId));
    try {
      await deleteFleetInvitation(invitationId);
      void mutate();
    } catch {
      // Already toasted and logged by the mutation hook; caught only to
      // avoid an unhandled promise rejection here.
    } finally {
      setDeletingIds((prev) => {
        const next = new Set(prev);
        next.delete(invitationId);
        return next;
      });
    }
  };

  return (
    <FleetInvitationsTableView
      data={data}
      deletingIds={deletingIds}
      fleet={fleet}
      isLoading={isLoading}
      isValidating={isValidating}
      mutate={mutate}
      onDelete={handleDelete}
    />
  );
};

const FleetInvitationsTableAll = () => {
  const { data, isLoading, mutate, isValidating } = useFleetInvitations();
  return (
    <FleetInvitationsTableView
      data={data}
      fleet={undefined}
      isLoading={isLoading}
      isValidating={isValidating}
      mutate={mutate}
    />
  );
};

type FleetInvitationsTableViewProps = {
  fleet: FleetV2Response | undefined;
  data: ReturnType<typeof useFleetInvitations>['data'];
  isLoading: boolean;
  isValidating: boolean;
  mutate: () => void;
  onDelete?: (invitationId: number) => Promise<void>;
  deletingIds?: Set<number>;
};

const FleetInvitationsTableView = ({
  fleet,
  data,
  isLoading,
  isValidating,
  mutate,
  onDelete,
  deletingIds,
}: FleetInvitationsTableViewProps) => {
  const [_addingInvitedWorkspaceId, setAddingInvitedWorkspaceId] = useState<number | null>(null);
  const addFleetInvitation = useApiMutation(
    'fleet-add-invitation',
    async (
      callApi,
      {
        fleetId,
        workspaceId,
        invitedWorkspaceId,
      }: { fleetId: number; workspaceId: number; invitedWorkspaceId: number }
    ) =>
      await callApi((api) =>
        api.fleets.postV1WorkspacesWorkspaceidFleetsFleetidInvitations(
          {
            fleetId,
            workspaceId,
          },
          {
            invitedWorkspaceId: invitedWorkspaceId,
          }
        )
      )
  );
  const { data: dataWorkspaces } = useAllWorkspaces();

  const columns = useMemo(
    () => fleetInvitationTableColumns(onDelete, deletingIds),
    [onDelete, deletingIds]
  );

  if (!data) {
    return <Loader />;
  }

  const handleAddWorkspaceInvitation = async (workspaceId: number) => {
    if (fleet) {
      setAddingInvitedWorkspaceId(workspaceId);
      try {
        await addFleetInvitation({
          fleetId: fleet.id,
          workspaceId: fleet.workspaceId,
          invitedWorkspaceId: workspaceId,
        });
        mutate();
      } catch {
        // Already toasted and logged by the mutation hook; caught only to
        // avoid an unhandled promise rejection here.
      } finally {
        setAddingInvitedWorkspaceId(null);
      }
    }
  };

  return (
    <TableStoreProvider initialSorting={[{ desc: true, id: 'id' }]} name="Fleet Invitations">
      <Table
        addButton={() => (
          <Menu>
            <Tooltip label="Add a new fleet invitation for cohosting" placement="left">
              <Menu.Button
                as={Button}
                borderRightRadius="0px"
                isDisabled={!fleet}
                leftIcon={<Icon as={PlusBold} />}
                size="sm"
                variant="outline"
              >
                Invite
              </Menu.Button>
            </Tooltip>
            <Portal>
              <Menu.List maxH="min(300px, calc(100dvh - 2rem))" overflowY="auto">
                {!dataWorkspaces || dataWorkspaces.workspaces.length === 0 ? (
                  <Loader />
                ) : (
                  dataWorkspaces.workspaces.map((workspace) => (
                    <Menu.Item
                      isDisabled={
                        fleet?.workspaceId === workspace.id ||
                        data.invitations.find(
                          (invitation) => invitation.invitedWorkspaceId === workspace.id
                        )
                      }
                      key={workspace.id}
                      onClick={() => handleAddWorkspaceInvitation(workspace.id)}
                    >
                      <Flex alignItems="center" gap={2}>
                        <Text>
                          {workspace.name} / Workspace #{workspace.id}
                        </Text>
                      </Flex>
                    </Menu.Item>
                  ))
                )}
              </Menu.List>
            </Portal>
          </Menu>
        )}
        columns={columns}
        count={data.itemCount || 0}
        data={data.invitations.map((d) => ({
          id: d.id,
          createdByOid: d.createdByOid,
          updatedAt: d.updatedAt,
          fleetId: d.fleetId,
          invitedWorkspace: d.invitedWorkspaceId,
          state: d.state,
        }))}
        description={
          <Flex>
            <Text>
              To deploy to devices in other workspaces, an invitation needs to be sent to that
              particular workspace
            </Text>
          </Flex>
        }
        emptyMessage={fleet ? 'No invitations for this fleet' : 'No fleet invitations found'}
        filteredEmptyMessage="No fleet invitations match the filters"
        getRowProps={(row) => (deletingIds?.has(row.id) ? { opacity: 0.4 } : {})}
        isLoading={Boolean(isLoading || !data)}
        isValidating={isValidating}
        refresh={mutate}
        title="Fleet Invitations"
      />
    </TableStoreProvider>
  );
};
